// Measures real "time on ERP" for the Activity Log Users pages.
//
// Every 15s: if this tab is visible AND focused AND the person moved the
// mouse / typed / scrolled / touched in the last 60s, count 15 active seconds.
// Once a minute, send what was counted (if anything) with the current page.
// An open-but-idle tab, a background tab or a locked screen sends nothing,
// so background polling never inflates anyone's time. Renders nothing.
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TICK_MS = 15000;
const SEND_MS = 60000;
const IDLE_AFTER_MS = 60000;

export default function PresenceHeartbeat() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let lastInput = Date.now();
    let pending = 0;
    const onInput = () => { lastInput = Date.now(); };
    const events = ["mousemove", "mousedown", "keydown", "wheel", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onInput, { passive: true, capture: true }));

    const send = (keepalive = false) => {
      const token = localStorage.getItem("foundry-token");
      if (!pending || !token) return;
      const seconds = pending;
      pending = 0;
      fetch(`${API}/api/activity-logs/heartbeat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ path: pathRef.current, seconds }),
        keepalive,
      }).catch(() => {});
    };

    const tick = setInterval(() => {
      const engaged = document.visibilityState === "visible" && document.hasFocus() && Date.now() - lastInput < IDLE_AFTER_MS;
      if (engaged) pending += TICK_MS / 1000;
    }, TICK_MS);
    const flush = setInterval(() => send(), SEND_MS);
    const onHide = () => { if (document.visibilityState === "hidden") send(true); };
    document.addEventListener("visibilitychange", onHide);

    return () => {
      clearInterval(tick);
      clearInterval(flush);
      document.removeEventListener("visibilitychange", onHide);
      events.forEach((e) => window.removeEventListener(e, onInput, { capture: true }));
    };
  }, [isAuthenticated]);

  return null;
}
